import { TextField } from '@mui/material';
import React from 'react';

interface WorkspaceInputProps {
  /** Rendered as the empty string when null or undefined, so the field is never uncontrolled. */
  value: string | number | null | undefined;
  /** The raw string from the input. Number coercion belongs to the caller's form shape. */
  onChange: (value: string) => void;
  type?: string;
  helperText?: string;
  /** Pairs with helperText for inline validation, as the placeholder rename box does. */
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** For the rare full-width-in-a-narrow-cell case; layout otherwise comes from the grid. */
  fullWidth?: boolean;
  /**
   * Share the row equally with its siblings. The key/value attribute rows are a flex row of two
   * inputs and a delete button, where `fullWidth` alone would not divide the space. The sx lives
   * here rather than at four call sites so it stays one decision.
   */
  flex?: boolean;
  /** Chip editors commit on Enter. */
  onKeyDown?: React.KeyboardEventHandler;
  autoComplete?: string;
  /** Multi-line, growing from this many rows. For a paragraph such as a candidate summary. */
  minRows?: number;
}

/**
 * The inline editor inside a workspace section card.
 *
 * <p>Not the same control as `FormField`, and deliberately so. A form field announces itself: label
 * above, helper text below, space around it. This one replaces a value in place inside a grid of
 * read-mostly rows, under the row's own micro-label, so it carries no label and no margin of its
 * own. `WorkspaceFieldLabel` is what names it.</p>
 *
 * <p>It exists because that decision was written five times. `JobWorkspace`, `CompanyWorkspace`,
 * `JobSeekerWorkspace`, `EmployerWorkspace` and `UserWorkspace` each declared a local `input()`
 * helper returning a bare `TextField`, identical but for how each one coerced its value. That is one
 * decision repeated, not five decisions, and it is the shape a raw-TextField guardrail would
 * otherwise have to carve an exception for in five files.</p>
 *
 * <p>The callers keep their thin local wrapper, because binding a key to `form` and `setField` is
 * genuinely per-file. What they no longer each own is the control.</p>
 */
const WorkspaceInput: React.FC<WorkspaceInputProps> = ({
  value,
  onChange,
  type,
  helperText,
  error = false,
  placeholder,
  disabled = false,
  fullWidth = true,
  flex = false,
  onKeyDown,
  autoComplete,
  minRows,
}) => (
  <TextField
    size="small"
    fullWidth={fullWidth}
    type={type}
    value={value ?? ''}
    helperText={helperText}
    error={error}
    placeholder={placeholder}
    disabled={disabled}
    autoComplete={autoComplete}
    multiline={minRows != null}
    minRows={minRows}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    sx={flex ? { flex: 1 } : undefined}
  />
);

export default WorkspaceInput;
